import { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import GetUser from "../services/GetUser";

/**
 * Componente ProtectedRoute que verifica los permisos del usuario antes de acceder a una ruta
 * @param {React.Component} element - El componente a renderizar si está autorizado
 * @param {string[]} allowedStatuses - Estados permitidos para acceder a esta ruta
 * @returns {React.Component}
 */
export default function ProtectedRoute({ element, allowedStatuses = [] }) {
    const [isLoading, setIsLoading] = useState(true);
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [userBlocked, setUserBlocked] = useState(false);

    useEffect(() => {
        const verifyUserAccess = async () => {
            try {
                const crewCode = sessionStorage.getItem('crewCode');
                const token = localStorage.getItem('token');

                // Si no hay sesión, redirigir a login
                if (!crewCode || !token) {
                    setIsLoading(false);
                    return;
                }

                // Obtener datos del usuario
                const userData = await GetUser({ username: crewCode });

                if (!userData || !Array.isArray(userData) || userData.length === 0) {
                    setIsLoading(false);
                    return;
                }

                const userStatus = userData[0].Confirmado;

                // Si usuario está bloqueado, denegar acceso completamente
                if (userStatus === 'No') {
                    setUserBlocked(true);
                    setIsLoading(false);
                    return;
                }

                // Verificar si el estado del usuario está en la lista de permitidos
                if (allowedStatuses.includes(userStatus)) {
                    if (userStatus === 'RE' && userData[0].policyChangeRequired) {
                        sessionStorage.setItem('policyChangeRequired', 'true');
                        if (userData[0].forced_reason) {
                            sessionStorage.setItem('forcedReason', userData[0].forced_reason);
                        }
                    }
                    setIsAuthorized(true);
                } else {
                    // Redirigir al login si no tiene permisos
                    setIsAuthorized(false);
                }
            } catch (err) {
                console.error('Error verificando acceso a ruta:', err);
                setIsAuthorized(false);
            } finally {
                setIsLoading(false);
            }
        };

        verifyUserAccess();
    }, [allowedStatuses]);

    // Mientras se verifica, mostrar loader
    if (isLoading) {
        return (
            <div style={{
                minHeight: '100vh',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#fff',
                fontSize: '18px'
            }}>
                Verificando acceso...
            </div>
        );
    }

    // Si usuario está bloqueado
    if (userBlocked) {
        return (
            <div style={{
                minHeight: '100vh',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
                color: '#fff'
            }}>
                <div style={{
                    textAlign: 'center',
                    padding: '40px 60px',
                    background: 'rgba(0, 0, 0, 0.2)',
                    borderRadius: '12px',
                    maxWidth: '500px'
                }}>
                    <h2 style={{ margin: '0 0 20px 0', fontSize: '28px' }}>⚠️ Acceso Denegado</h2>
                    <p style={{ margin: '0 0 15px 0', fontSize: '16px' }}>
                        Tu cuenta ha sido bloqueada. No tienes acceso a esta página.
                    </p>
                    <p style={{ margin: 0, fontSize: '14px', opacity: 0.9 }}>
                        Contacta con el departamento de supervisores.
                    </p>
                </div>
            </div>
        );
    }

    // Si no está autorizado, redirigir a login
    if (!isAuthorized) {
        return <Navigate to="/" replace />;
    }

    // Si está autorizado, renderizar el componente
    return element;
}
